import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { storage, calculateCartTotal, calculateCartItemCount, generateUniqueId, isPreOrder } from '../../../utils/helpers';
import { DEFAULTS, ERROR_MESSAGES } from '../../../utils/constants';
type CartItem = {
  id: string;
  productId: number;
  name: string;
  price: number;
  quantity: number;
  image: string;
  stockQuantity?: number;
  stockStatus: 'instock' | 'outofstock' | 'onbackorder';
  variationId?: number;
  variationName?: string;
  variationAttributes?: any;
  bundleSelections?: any[];
  soldIndividually?: boolean;
  isPreOrder?: boolean;
};

type Cart = { items: CartItem[]; total: number; itemCount: number };

type Product = {
  id: number;
  name: string;
  price: number;
  images: string[];
  stockQuantity?: number;
  stockStatus: 'instock' | 'outofstock' | 'onbackorder';
  type?: string;
  variations?: any[];
  soldIndividually?: boolean;
  tags?: Array<{ id: number; name: string; slug: string }>;
};

 

interface CartContextType {
  cart: Cart;
  loading: boolean;
  error: string | null;
  addToCart: (product: Product, quantity?: number, variationId?: number, variationName?: string, bundleSelections?: any[], variationAttributes?: any) => void;
  updateCartItem: (productId: number, quantity: number) => void;
  removeFromCart: (productId: number) => void;
  clearCart: () => void;
  getCartItem: (productId: number) => CartItem | undefined;
  isInCart: (productId: number) => boolean;
  getCartItemQuantity: (productId: number) => number;
  clearError: () => void;
  // Global popup state
  popupOpen: boolean;
  popupMessage: string;
  showPopup: (message: string) => void;
  hidePopup: () => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<Cart>(() => {
    const savedCart = storage.get(DEFAULTS.CART_STORAGE_KEY);
    return savedCart || { items: [], total: 0, itemCount: 0 };
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [popupMessage, setPopupMessage] = useState<string>('');

  useEffect(() => {
    storage.set(DEFAULTS.CART_STORAGE_KEY, cart);
  }, [cart]);

  const showPopup = useCallback((message: string) => {
    setPopupMessage(message);
    setPopupOpen(true);
  }, []);
  
  const hidePopup = useCallback(() => {
    setPopupOpen(false);
  }, []);

  const addToCart = useCallback((product: Product, quantity: number = 1, variationId?: number, variationName?: string, bundleSelections?: any[], variationAttributes?: any) => {
    setLoading(true);
    setError(null);
    
    try {
      // For variable products with variations, use the variation data
      let effectivePrice = product.price;
      let effectiveStockStatus = product.stockStatus;
      let effectiveStockQuantity = product.stockQuantity;
      let effectiveImage = product.images[0] || '';
      let effectiveName = product.name;
      let effectiveSoldIndividually = product.soldIndividually;
      
      // If this is a variation, find the variation details
      if (variationId && product.type === 'variable' && product.variations) {
        const variation = product.variations.find(v => v.id === variationId);
        if (variation) {
          effectivePrice = variation.price;
          effectiveStockStatus = variation.stockStatus;
          effectiveStockQuantity = variation.stockQuantity;
          effectiveImage = variation.image || product.images[0] || '';
          effectiveName = `${product.name} - ${variationName || variation.displayName}`;
          effectiveSoldIndividually = variation.soldIndividually;
        }
      }
      
      // Pre-order products are sold before stock arrives, so they intentionally
      // skip every stock check below.
      const productIsPreOrder = isPreOrder(product);

      // VALIDATE using LIVE product data (not stale cart data)
      if (!productIsPreOrder && effectiveStockStatus === 'outofstock') {
        setError(ERROR_MESSAGES.PRODUCT_OUT_OF_STOCK);
        setLoading(false);
        return;
      }
      
      if (!productIsPreOrder && effectiveStockQuantity !== undefined && quantity > effectiveStockQuantity) {
        setError(`Only ${effectiveStockQuantity} items available in stock.`);
        setLoading(false);
        return;
      }
      
      // Check if item already in cart and validate new total quantity
      setCart(prevCart => {
        // For variations, check if the same variation is already in cart
        const existingItem = variationId 
          ? prevCart.items.find(item => item.productId === product.id && item.variationId === variationId)
          : prevCart.items.find(item => item.productId === product.id && !item.variationId);
        
        // Check sold individually restriction
        if (effectiveSoldIndividually) {
          if (existingItem && existingItem.quantity >= 1) {
            setError('This item can only be purchased once per order.');
            setLoading(false);
            return prevCart; // Return unchanged cart
          }
          // Also check if trying to add more than 1 of a sold individually item
          if (quantity > 1) {
            setError('This item can only be purchased once per order.');
            setLoading(false);
            return prevCart; // Return unchanged cart
          }
        }
        
        if (existingItem) {
          const newQuantity = existingItem.quantity + quantity;
          if (!productIsPreOrder && effectiveStockQuantity !== undefined && newQuantity > effectiveStockQuantity) {
            setError(`Cannot add ${quantity} more. Only ${effectiveStockQuantity - existingItem.quantity} more available.`);
            setLoading(false);
            return prevCart; // Return unchanged cart
          }
        }
        
        // Safe to update cart now
        if (existingItem) {
          const newQuantity = existingItem.quantity + quantity;
          const updatedItems = prevCart.items.map(item => 
            (variationId ? (item.productId === product.id && item.variationId === variationId) : (item.productId === product.id && !item.variationId))
              ? { ...item, quantity: newQuantity } : item
          );
          // Show popup for quantity update
          showPopup(effectiveName);
          return { items: updatedItems, total: calculateCartTotal(updatedItems), itemCount: calculateCartItemCount(updatedItems) };
        } else {
          const newItem: CartItem = {
            id: generateUniqueId(),
            productId: product.id,
            name: effectiveName,
            price: effectivePrice,
            quantity,
            image: effectiveImage,
            stockQuantity: effectiveStockQuantity,
            stockStatus: effectiveStockStatus,
            variationId,
            variationName,
            variationAttributes,
            bundleSelections,
            soldIndividually: effectiveSoldIndividually,
            isPreOrder: productIsPreOrder,
          };
          const newItems = [...prevCart.items, newItem];
          // Show popup for new item
          showPopup(effectiveName);
          return { items: newItems, total: calculateCartTotal(newItems), itemCount: calculateCartItemCount(newItems) };
        }
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.API_ERROR;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [showPopup]);

  const removeFromCart = useCallback((productId: number) => {
    setLoading(true);
    setError(null);
    try {
      setCart(prevCart => {
        const updatedItems = prevCart.items.filter(i => i.productId !== productId);
        return { items: updatedItems, total: calculateCartTotal(updatedItems), itemCount: calculateCartItemCount(updatedItems) };
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.API_ERROR;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCartItem = useCallback((productId: number, quantity: number) => {
    setLoading(true);
    setError(null);
    
    try {
      if (quantity <= 0) { 
        removeFromCart(productId); 
        return; 
      }
      
      setCart(prevCart => {
        const item = prevCart.items.find(i => i.productId === productId);
        if (!item) {
          setError('Item not found in cart');
          setLoading(false);
          return prevCart;
        }
        
        // Check sold individually restriction
        if (item.soldIndividually && quantity > 1) {
          setError('This item can only be purchased once per order.');
          setLoading(false);
          return prevCart;
        }
        
        // Pre-orders have no stock ceiling - stock has not arrived yet
        if (!item.isPreOrder && item.stockQuantity !== undefined && quantity > item.stockQuantity) {
          setError(`Only ${item.stockQuantity} items available in stock`);
          setLoading(false);
          return prevCart;
        }
        
        const updatedItems = prevCart.items.map(i => i.productId === productId ? { ...i, quantity } : i);
        return { items: updatedItems, total: calculateCartTotal(updatedItems), itemCount: calculateCartItemCount(updatedItems) };
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.API_ERROR;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setCart({ items: [], total: 0, itemCount: 0 });
  }, []);

  const getCartItem = useCallback((productId: number): CartItem | undefined => cart.items.find(i => i.productId === productId), [cart.items]);
  const isInCart = useCallback((productId: number): boolean => cart.items.some(i => i.productId === productId), [cart.items]);
  const getCartItemQuantity = useCallback((productId: number): number => (cart.items.find(i => i.productId === productId)?.quantity ?? 0), [cart.items]);
  const clearError = useCallback(() => setError(null), []);

  const value: CartContextType = { 
    cart, 
    loading, 
    error, 
    addToCart, 
    updateCartItem, 
    removeFromCart, 
    clearCart, 
    getCartItem, 
    isInCart, 
    getCartItemQuantity, 
    clearError,
    popupOpen,
    popupMessage,
    showPopup,
    hidePopup
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
}


